import React from "react";
import { FeaturePageShell, FeatureVideo } from "./FeaturePageShell";
import weave1 from "./assets/living-weave/weave1.png";
import weave2 from "./assets/living-weave/weave2.png";
import weave3 from "./assets/living-weave/weave3.png";
import globalVideo from "./assets/living-weave/Global.mp4";
import "./LivingWeavePage.css";

const persistentFeatures = [
  {
    label: "Universal Persistent URL",
    text: "Your media lives on a single, permanent link. New perspectives, fresh updates, and replacement scenes hot-swap into place seamlessly without breaking web embeds, altering links, or fragmenting your audience.",
  },
  {
    label: "Historical Snapshots",
    text: "Key narrative milestones are preserved as chronological snapshots. Viewers can revisit earlier chapters of a project's history or stay anchored in the latest live iteration.",
  },
  {
    label: "Modular .weave Architecture",
    text: "Built on an open narrative container, Weavevo decouples video slots, dynamic audio stems, and narrative metadata, allowing surgical scene updates without re-rendering the full production.",
  },
];

const aiPipeline = [
  {
    icon: "bi-person-video3",
    title: "Creator & Weaver Synergy",
    text: "The Lead Creator sets the vision, blueprint, and open slots, while distributed Weavers across the globe contribute authentic, hyper-local perspectives.",
  },
  {
    icon: "bi-bezier2",
    title: "Connection AI",
    text: "Normalizes disparate color grades, aligns acoustic transients, and verifies spatiotemporal consistency across incoming mobile and broadcast footage.",
  },
  {
    icon: "bi-stars",
    title: "Generation AI",
    text: "Adapts synthesized voice-overs, generates real-time multilingual subtitles, and retimes narrative pacing the moment a contribution is accepted.",
  },
  {
    icon: "bi-lightning-charge",
    title: "Frictionless Post-Production",
    text: "Creator slot approval triggers instant stream assimilation alongside automated royalty distribution via Smart Escrow.",
  },
];

const industries = [
  {
    title: "Hospitality & Resorts",
    icon: "bi-buildings",
    text: "Showcase seasonal menus, wellness programs, and local excursions captured by on-site instructors and regional guides without re-shooting promotional campaigns.",
  },
  {
    title: "Real Estate & Urban Spaces",
    icon: "bi-house-heart",
    text: "Document properties from architectural genesis to final staging, or highlight surrounding neighborhood culture through local merchant contributions.",
  },
  {
    title: "Sports & Live Events",
    icon: "bi-broadcast-pin",
    text: "Synchronize official broadcast angles, raw terrace crowd acoustics, and live match data into an interactive stream anchored to a single event URL.",
  },
  {
    title: "Culture, Brands & Enterprise",
    icon: "bi-globe2",
    text: "Power cultural exhibits, participatory documentaries, and internal company summits with living media hubs that stay updated effortlessly.",
  },
];

function SectionNumber({ children }) {
  return <span className="lw-sectionNumber">{children}</span>;
}

export default function LivingWeavePage() {
  return (
    <FeaturePageShell
      pageTitle="Living Weave Pipeline"
      currentPath="/living-weave-pipeline"
      eyebrow="WEAVEVO TECHNOLOGY · LIVING WEAVE PIPELINE"
      heroTitle="A single URL. Zero obsolescence."
      heroText="Video should never be a frozen, static file that becomes obsolete the moment it is exported. On Weavevo, a Weave is a modular, dynamic narrative organism that evolves over time."
      heroImage={weave1}
      heroAlt="Living Weave persistent stream with snapshots and hot-swapped media"
      heroClassName="fp-livingWeavePage"
      finalEyebrow="LIVING MEDIA, BY DEFAULT"
      finalTitle="One platform. Infinite stories."
      finalText="Because a Weave lives and adapts continuously, it serves any industry requiring persistent, collaborative, real-time storytelling."
    >
      <section className="lw-section lw-persistent">
        <div className="lw-shell">
          <div className="lw-sectionHead">
            <SectionNumber>01</SectionNumber>
            <div>
              <span className="lw-kicker">Living by default</span>
              <p>
                A Weave behaves like a living narrative container: one address, many iterations, and no need to break the audience every time the story changes.
              </p>
            </div>
          </div>

          <div className="lw-persistentGrid">
            <div className="lw-versionConsole" aria-label="Living Weave evolution timeline">
              <div className="lw-consoleBar">
                <span className="lw-consoleDot is-live" />
                <span>weavevo.com/live</span>
                <strong>LIVE · v3.2</strong>
              </div>
              <img src={weave1} alt="Persistent URL and Living Weave snapshot timeline" loading="lazy" />
            </div>

            <div className="lw-featureStack">
              {persistentFeatures.map((feature, index) => (
                <article className="lw-featureCard" key={feature.label}>
                  <span className="lw-featureIndex">0{index + 1}</span>
                  <div>
                    <h3>{feature.label}</h3>
                    <p>{feature.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lw-aiStage" aria-labelledby="lw-ai-title">
        <div className="lw-aiGlow lw-aiGlowOne" />
        <div className="lw-aiGlow lw-aiGlowTwo" />
        <div className="lw-shell lw-aiInner">
          <div className="lw-sectionHead is-dark">
            <SectionNumber>02</SectionNumber>
            <div>
              <span className="lw-kicker">Creators · Weavers · Weavevo AI</span>
              <h2 id="lw-ai-title">The Collaborative Pipeline</h2>
              <p>
                A Living Weave unites decentralized, on-the-ground human capture with automated AI orchestration.
              </p>
            </div>
          </div>

          <div className="lw-aiGrid">
            <div className="lw-aiVisual">
              <div className="lw-imageHalo" />
              <img src={weave2} alt="Weavevo mobile collaboration flow connected to the Weavevo AI Engine" loading="lazy" />
              <div className="lw-liveBadge"><span /> HUMAN CAPTURE + WEAVEVO AI</div>
            </div>

            <div className="lw-pipeline" aria-label="Living Weave collaborative processing pipeline">
              <div className="lw-pipelineSpine" aria-hidden="true" />
              {aiPipeline.map((step, index) => (
                <article className="lw-pipelineNode" key={step.title}>
                  <div className="lw-nodeOrb"><i className={`bi ${step.icon}`} aria-hidden="true" /></div>
                  <div className="lw-nodeCopy">
                    <small>STAGE {String(index + 1).padStart(2, "0")}</small>
                    <h3>{step.title}</h3>
                    <p>{step.text}</p>
                  </div>
                  <span className="lw-nodeState">SYNCED</span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lw-section lw-industries" aria-labelledby="lw-industries-title">
        <div className="lw-shell">
          <div className="lw-sectionHead">
            <SectionNumber>03</SectionNumber>
            <div>
              <span className="lw-kicker">Boundless flexibility</span>
              <h2 id="lw-industries-title">Cross-Industry Living Media</h2>
              <p>
                Because a Weave lives and adapts continuously, it serves any industry requiring persistent, collaborative, real-time storytelling.
              </p>
            </div>
          </div>

          <div className="lw-industryHero">
            <img src={weave3} alt="Living Weave examples across sports, hospitality, culture and real estate" loading="lazy" />
            <div className="lw-industryHeroCaption">
              <span>ONE PLATFORM</span>
              <strong>Infinite stories · One persistent media layer</strong>
            </div>
          </div>

          <div className="lw-industryGrid">
            {industries.map((item, index) => (
              <article className="lw-industryCard" key={item.title}>
                <div className="lw-industryIcon"><i className={`bi ${item.icon}`} aria-hidden="true" /></div>
                <small>0{index + 1}</small>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="lw-motionStage" aria-labelledby="lw-motion-title">
        <div className="lw-shell lw-motionGrid">
          <div className="lw-motionCopy">
            <span className="lw-kicker">Persistent · collaborative · adaptive</span>
            <h2 id="lw-motion-title">See the Living Weave in motion.</h2>
            <p>
              New perspectives can arrive, be harmonized, accepted and assimilated without replacing the link that already carries the audience.
            </p>
            <div className="lw-motionStats" aria-label="Living Weave concepts">
              <span><strong>1</strong> persistent URL</span>
              <span><strong>N</strong> evolving snapshots</span>
              <span><strong>∞</strong> contributor perspectives</span>
            </div>
          </div>
          <FeatureVideo src={globalVideo} poster={weave3} className="lw-globalVideo" />
        </div>
      </section>
    </FeaturePageShell>
  );
}
